import React, { useState, useEffect } from 'react';
import { ArrowRight, MessageCircle, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { heroContent as mockHero, contactInfo as mockContact } from '../../data/mock';
import { cmsService } from '../../services/cmsService';

const Hero = () => {
  const [hero, setHero] = useState({
    title: mockHero.title,
    subtitle: mockHero.subtitle,
    cta_text: mockHero.cta,
    stat1_value: '+200',
    stat1_label: 'Empresas asesoradas',
    stat2_value: '+$50M',
    stat2_label: 'Desembolsados',
    stat3_value: '24h',
    stat3_label: 'Compromiso a desembolsar tras aprobación',
  });

  useEffect(() => {
    cmsService.getHero().then(data => setHero(data)).catch(() => {});
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-br from-slate-50 via-white to-sky-50" data-testid="hero-section">
      {/* Background Decorations */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-[#0092ca]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#0092ca]/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-[#0092ca]/5 to-transparent rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,146,202,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(0,146,202,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="text-left space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#0092ca]/10 rounded-full">
              <span className="w-2 h-2 bg-[#0092ca] rounded-full animate-pulse" />
              <span className="text-sm font-medium text-[#0092ca]">Factoring en Panamá</span>
            </div>

            {/* Title */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight" data-testid="hero-title">
              {hero.title.split(' ').slice(0, 2).join(' ')}{' '}
              <span className="text-[#0092ca] relative">
                {hero.title.split(' ').slice(2, 4).join(' ')}
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none">
                  <path d="M2 10C50 4 150 4 198 10" stroke="#0092ca" strokeWidth="3" strokeLinecap="round" opacity="0.3" />
                </svg>
              </span>{' '}
              <span className="block mt-2">{hero.title.split(' ').slice(4).join(' ')}</span>
            </h1>

            {/* Subtitle */}
            <p className="text-lg sm:text-xl text-gray-600 max-w-xl leading-relaxed" data-testid="hero-subtitle">
              {hero.subtitle}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/50765865503?text=Hola,%20me%20interesa%20conocer%20más%20sobre%20sus%20servicios%20de%20factoring."
                target="_blank"
                rel="noopener noreferrer">
                <Button data-testid="hero-whatsapp-btn" className="bg-[#25D366] hover:bg-[#1da851] text-white px-8 py-6 rounded-full text-lg font-semibold shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 group">
                  <svg className="w-6 h-6 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  {hero.cta_text}
                </Button>
              </a>
              <a href="#proceso">
                <Button
                  variant="outline"
                  className="border-2 border-gray-300 hover:border-[#0092ca] text-gray-700 hover:text-[#0092ca] px-8 py-6 rounded-full text-lg font-semibold transition-all duration-300 group">
                  <Play className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                  Cómo Funciona
                </Button>
              </a>
            </div>

            {/* Trust indicators */}
            <div className="pt-8 border-t border-gray-200">
              <p className="!text-sm mb-4 text-gray-500">Nuestro equipo cuenta con:</p>
              <div className="flex items-center gap-8">
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-900" data-testid="hero-stat1-value">{hero.stat1_value}</p>
                  <p className="!text-xs text-gray-500">{hero.stat1_label}</p>
                </div>
                <div className="w-px h-10 bg-gray-200" />
                <div className="text-center">
                  <p className="!font-bold !text-2xl text-gray-900" data-testid="hero-stat2-value">{hero.stat2_value}</p>
                  <p className="!text-xs text-gray-500">{hero.stat2_label}</p>
                </div>
                <div className="w-px h-10 bg-gray-200" />
                <div className="text-center">
                  <p className="!font-bold !text-2xl text-gray-900" data-testid="hero-stat3-value">{hero.stat3_value}</p>
                  <p className="!text-xs text-gray-500">{hero.stat3_label}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Visual */}
          <div className="relative hidden lg:block">
            <div className="relative">
              {/* Main card */}
              <div className="bg-white rounded-3xl shadow-2xl p-8 transform rotate-3 hover:rotate-0 transition-transform duration-500">
                <div className="bg-gradient-to-br from-[#0092ca] to-[#006d96] rounded-2xl p-8 text-white">
                  <div className="flex items-center justify-between mb-6">
                    <span className="text-sm opacity-80">Factura #2024-0847</span>
                    <span className="px-3 py-1 bg-white/20 rounded-full text-xs">Aprobada</span>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm opacity-80">Valor de Factura</p>
                      <p className="text-3xl font-bold">$45,000.00</p>
                    </div>
                    <div className="flex gap-4">
                      <div className="flex-1 bg-white/10 rounded-xl p-4">
                        <p className="text-xs opacity-80">Adelanto (90%)</p>
                        <p className="text-xl font-semibold">$40,500.00</p>
                      </div>
                      <div className="flex-1 bg-white/10 rounded-xl p-4">
                        <p className="!text-xs opacity-80">En 24 horas</p>
                        <p className="text-xl font-semibold">Disponible</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating elements */}
              <div className="absolute -top-4 -left-4 bg-white rounded-2xl shadow-xl p-4 animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="!font-semibold text-gray-900">Desembolso Rápido</p>
                    <p className="!text-sm text-gray-500">En 24 horas tras afiliación</p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl p-4 animate-float-delayed">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#0092ca]/10 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#0092ca]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Sin Deuda</p>
                    <p className="text-sm text-gray-500">No es préstamo</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
        <span className="text-sm text-gray-400">Descubre más</span>
        <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>
    </section>
  );
};

export default Hero;
